// Helpers condivisi per la sezione Business (CRM interno: Contatti / Clienti).
// Separati da lib/contactHelpers.js che gestisce i contatti pubblici (whatsapp/telefono
// mostrati nella pagina "Contatti" dell'app) — dominio diverso, nessuna relazione.

export const SOCIAL_PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'tiktok', label: 'TikTok' },
];

export const CONTACT_ORIGINS = [
  { value: 'offline', label: 'Offline' },
  { value: 'social', label: 'Social' },
];

export const CONTACT_STATUSES = [
  { value: 'da_conoscere', label: 'Da conoscere' },
  { value: 'relazione', label: 'Relazione' },
  { value: 'proposta_fatta', label: 'Proposta fatta' },
  { value: 'in_valutazione', label: 'In valutazione' },
  { value: 'pronta_a_partire', label: 'Pronta a partire' },
  { value: 'non_ora', label: 'Non ora' },
];

export function contactStatusLabel(value) {
  return CONTACT_STATUSES.find((s) => s.value === value)?.label || null;
}

export const TEAM_STATUSES = [
  { value: 'supervisore', label: 'SUPERVISORE' },
  { value: 'supervisore_attivo', label: 'SUPERVISORE ATTIVO' },
  { value: 'distributore', label: 'DISTRIBUTORE' },
  { value: 'focus_600', label: 'FOCUS 600' },
];

export function teamStatusLabel(value) {
  return TEAM_STATUSES.find((s) => s.value === value)?.label || null;
}

/** Classi Tailwind per il badge status in Il Mio Team. */
export function teamStatusBadgeClass(value) {
  switch (value) {
    case 'supervisore':
      return 'bg-orange-500 text-white';
    case 'supervisore_attivo':
      return 'bg-red-600 text-white';
    case 'distributore':
      return 'bg-yellow-400 text-yellow-900';
    case 'focus_600':
      return 'bg-green-500 text-white';
    default:
      return 'bg-zinc-100 text-zinc-700';
  }
}

/** Classi Tailwind per il badge status in Contatti Business. */
export function contactStatusBadgeClass(value) {
  switch (value) {
    case 'pronta_a_partire':
      return 'bg-green-50 text-green-700';
    case 'proposta_fatta':
    case 'in_valutazione':
      return 'bg-yellow-50 text-yellow-700';
    case 'relazione':
      return 'bg-pink-50 text-pink-700';
    case 'da_conoscere':
      return 'bg-sky-50 text-sky-700';
    case 'non_ora':
      return 'bg-red-50 text-red-700';
    default:
      return 'bg-zinc-100 text-zinc-700';
  }
}

export function socialPlatformLabel(value) {
  return SOCIAL_PLATFORMS.find((p) => p.value === value)?.label || value || '';
}

export function socialProfileUrl(platform, handle) {
  const clean = (handle || '').trim().replace(/^@/, '');
  if (!platform || !clean) return null;
  const encoded = encodeURIComponent(clean);
  if (platform === 'instagram') return `https://instagram.com/${encoded}`;
  if (platform === 'tiktok') return `https://tiktok.com/@${encoded}`;
  if (platform === 'facebook') return `https://facebook.com/${encoded}`;
  return null;
}

export function formatBusinessDate(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate.length <= 10 ? `${isoDate}T00:00:00` : isoDate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function todayISODate() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Contatti/Clienti: scegliere una data la conferma subito, senza tasto Aggiungi. */
export function addReminderDate(dates, d) {
  if (!d) return Array.isArray(dates) ? dates : [];
  const current = Array.isArray(dates) ? dates : [];
  if (current.includes(d)) return current;
  return [...current, d].sort();
}

/** Il Mio Team: scegliere una data crea il promemoria; l'oggetto si scrive accanto. */
export function addTeamReminder(reminders, date) {
  if (!date) return Array.isArray(reminders) ? reminders : [];
  const current = Array.isArray(reminders) ? reminders : [];
  if (current.some((r) => r.date === date)) return current;
  return [...current, { date, subject: '' }].sort((a, b) => a.date.localeCompare(b.date));
}

export function setTeamReminderSubject(reminders, date, subject) {
  return (reminders || []).map((r) => (r.date === date ? { ...r, subject } : r));
}

function businessWhatsAppDigits(phone) {
  return (phone || '').trim().replace(/[\s\-().]/g, '').replace(/^\+/, '');
}

/** Link web (wa.me) alla chat WhatsApp per un numero salvato in Contatti/Clienti Business. */
export function businessWhatsAppUrl(phone) {
  const digits = businessWhatsAppDigits(phone);
  return digits ? `https://wa.me/${digits}` : null;
}

/** Link nativo (protocollo whatsapp://) per aprire l'app desktop/mobile installata.
 * Usa lo stesso schema sia WhatsApp che WhatsApp Business (il sistema apre quella
 * impostata come predefinita per i link "whatsapp"). */
export function businessWhatsAppAppUrl(phone) {
  const digits = businessWhatsAppDigits(phone);
  return digits ? `whatsapp://send?phone=${digits}` : null;
}

/**
 * Apre la chat WhatsApp per un numero Business tentando prima l'app installata sul
 * dispositivo (desktop o mobile, WhatsApp o WhatsApp Business — entrambe registrano lo
 * stesso protocollo whatsapp://), e solo se non risponde entro breve tempo (nessuna app
 * installata/associata) ricade su WhatsApp Web nel browser.
 *
 * Nota: se il link non apre l'app anche quando e' installata, sul PC il sistema
 * operativo non ha "whatsapp://" associato a quell'app — su Windows si puo' impostare
 * da Impostazioni > App > App predefinite, cercando il tipo di link "WHATSAPP".
 */
export function openBusinessWhatsApp(phone) {
  const appUrl = businessWhatsAppAppUrl(phone);
  const webUrl = businessWhatsAppUrl(phone);
  if (!appUrl || !webUrl) return;

  const tab = window.open('', '_blank');
  if (!tab) {
    window.open(webUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  let settled = false;
  const goWeb = () => {
    if (settled) return;
    settled = true;
    document.removeEventListener('visibilitychange', onHide);
    try { tab.location.href = webUrl; } catch (e) { /* la scheda potrebbe essere stata chiusa dall'utente */ }
  };
  // Se il nostro browser passa in background e' probabile che il sistema abbia aperto
  // l'app desktop in risposta al protocollo: in tal caso annulliamo il fallback web.
  const onHide = () => {
    if (document.hidden) {
      settled = true;
      document.removeEventListener('visibilitychange', onHide);
      try { tab.close(); } catch (e) { /* noop */ }
    }
  };
  document.addEventListener('visibilitychange', onHide);

  try {
    tab.location.href = appUrl;
  } catch (e) {
    goWeb();
    return;
  }
  setTimeout(goWeb, 900);
}

function socialWriteFields(form) {
  const handle = (form?.social_handle || '').trim();
  const platform = handle ? (form?.social_platform || null) : null;
  return {
    social_platform: platform,
    social_handle: platform ? handle : '',
  };
}

function personWriteFields(form) {
  return {
    nome: (form?.nome || '').trim(),
    cognome: (form?.cognome || '').trim(),
    telefono: (form?.telefono || '').trim(),
    ...socialWriteFields(form),
    note: form?.note || '',
  };
}

/** Payload pulito per create/update cliente: niente campi extra del documento Mongo
 * (id, created_by, …) e social incompleto trattato come assente. */
export function clientWritePayload(form) {
  return {
    ...personWriteFields(form),
    rating: Number(form?.rating) || 0,
    reminder_dates: Array.isArray(form?.reminder_dates) ? form.reminder_dates : [],
  };
}

export function contactWritePayload(form) {
  return {
    ...personWriteFields(form),
    origine: form?.origine || null,
    status: form?.status || null,
    azienda_attuale: (form?.azienda_attuale || '').trim(),
    data_contatto: form?.data_contatto || null,
    rating: Number(form?.rating) || 0,
    reminder_dates: Array.isArray(form?.reminder_dates) ? form.reminder_dates : [],
  };
}

export function teamWritePayload(form) {
  return {
    ...personWriteFields(form),
    status: form?.status || null,
    data_iscrizione: form?.data_iscrizione || null,
    reminders: Array.isArray(form?.reminders) ? form.reminders : [],
  };
}

export function apiDetailMessage(detail, fallback = 'Operazione non riuscita') {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((x) => x.msg || x.message).filter(Boolean);
    return msgs.join('. ') || fallback;
  }
  return fallback;
}

export function fullName(entity) {
  return [entity?.nome, entity?.cognome].filter(Boolean).join(' ').trim();
}

export function initials(entity) {
  const n = (entity?.nome || '').trim()[0] || '';
  const c = (entity?.cognome || '').trim()[0] || '';
  return `${n}${c}`.toUpperCase() || '?';
}
